/**
 * Runs — "what went wrong". Pick a run, filter to its failures, and read one
 * case at a time: what should have happened, what did, every step in plain
 * words with its screenshot, and the evidence behind it. From here a failure
 * becomes a bug, is run again, or — when the test itself is wrong — is edited.
 * Each case also tells what happened NEXT: sent to Support automatically or
 * decided by a person, Support's progress, the fix, its release, and the run
 * that passed it again (`/runs/{id}/outcomes`) — and a case that passes now
 * says which earlier failure a fix resolved.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import toast from 'react-hot-toast'
import { Download, ExternalLink, Play, RotateCcw, Sparkles, Square, Ticket, Trash2 } from 'lucide-react'
import Button from '@/components/common/Button'
import { useConfirm } from '@/components/common/ConfirmDialog'
import {
  apiError, buildReport, cancelRun, deleteRun, getCaseResult, getRun, getRunJsonFile, getRunOutcomes,
  listRuns, raiseTicket, runFileUrl, syncDefects,
  type CaseOutcome, type CaseResult, type CaseStatus, type DefectStory, type RunCaseSummary, type RunDetail,
  type RunSummary,
} from '@/api/uiTesting'
import type { Focus, TestingNav } from './TestingPage'
import { isLive } from './TestingPage'
import CaseEditor from './CaseEditor'
import {
  Badge, Lightbox, NumberDot, ShareBar, TONE, Thumb, ago, caseTone, fmtTime, inputStyle, labelCaps,
  mutedText, panel, took, type Tone,
} from './shared'
import { isCheck, nice, shouldHappen, stepText } from './plainSteps'

interface Props {
  project: string
  canTest: boolean
  /** Developer / project manager: fix a failure in place; QC raises a ticket. */
  canFix: boolean
  focus: Focus
  nav: TestingNav
  onChanged: () => Promise<void> | void
  onRunAll: () => void
  running: boolean
  noCases: boolean
}

const POLL_MS = 2000
type Filter = CaseStatus | 'all'

const STATUS_WORD: Record<string, string> = {
  passed: 'Passed', failed: 'Failed', blocked: 'Couldn’t run', skipped: 'Not run',
}

function runLabel(r: RunSummary): string {
  const c = r.counts || {}
  const when = r.started_at ? new Date(r.started_at).toLocaleString([], {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  }) : r.run_id
  const where = r.driver === 'chrome' ? ' · in your browser' : ''
  if (isLive(r.status)) return `${when} · running ${r.done ?? 0}/${r.total ?? 0}${where}`
  const parts = [`${c.passed ?? 0} passed`, `${c.failed ?? 0} failed`]
  if (c.blocked) parts.push(`${c.blocked} couldn’t run`)
  return `${when} · ${r.total ?? 0} cases · ${parts.join(', ')}${r.status === 'cancelled' ? ' (cancelled)' : ''}${where}`
}

export default function RunsTab({ project, canTest, canFix, focus, nav, onChanged, onRunAll, running, noCases }: Props) {
  const [runs, setRuns] = useState<RunSummary[] | null>(null)
  const [selected, setSelected] = useState<string | null>(focus.run ?? null)
  const [run, setRun] = useState<RunDetail | null>(null)
  const [filter, setFilter] = useState<Filter | null>(null)
  const [picked, setPicked] = useState<string | null>(focus.caseId ?? null)
  const [busy, setBusy] = useState<string | null>(null)
  const [editing, setEditing] = useState<string | null>(null)
  const [outcomes, setOutcomes] = useState<Record<string, CaseOutcome>>({})
  const { confirm, confirmDialog } = useConfirm()
  const timer = useRef<number | null>(null)

  const loadRuns = useCallback(async () => {
    try {
      const list = await listRuns(project)
      setRuns(list)
      setSelected((s) => s ?? list[0]?.run_id ?? null)
    } catch (err) {
      toast.error(apiError(err, 'Could not load the runs'))
      setRuns([])
    }
  }, [project])

  useEffect(() => { void loadRuns() }, [loadRuns])
  useEffect(() => {
    if (focus.run) { setSelected(focus.run); setPicked(focus.caseId ?? null); setFilter(null) }
  }, [focus.run, focus.caseId])

  // Poll the selected run while it is queued/running; stop as soon as it is not.
  useEffect(() => {
    if (!selected) { setRun(null); return }
    let alive = true
    const tick = async () => {
      try {
        const r = await getRun(project, selected)
        if (!alive) return
        setRun(r)
        if (isLive(r.status)) {
          timer.current = window.setTimeout(tick, POLL_MS)
        } else {
          void loadRuns()
          void onChanged()
        }
      } catch (err) {
        if (alive) toast.error(apiError(err, 'Could not load the run'))
      }
    }
    void tick()
    return () => {
      alive = false
      if (timer.current) window.clearTimeout(timer.current)
    }
    // onChanged is re-created by the page on every reload; re-polling on it would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project, selected, loadRuns])

  // What happened to each case's failure — read once the run is finished, and
  // again after "Register as bug".
  const runDone = !!run && !isLive(run.status)
  const loadOutcomes = useCallback(async () => {
    if (!run || isLive(run.status)) return
    try {
      setOutcomes((await getRunOutcomes(project, run.run_id)).cases || {})
    } catch {
      setOutcomes({})   // the result still shows; only the follow-up is missing
    }
  }, [project, run?.run_id, runDone]) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setOutcomes({}); void loadOutcomes() }, [loadOutcomes])

  const counts = run?.counts || {}
  // Open on the failures when there are any, else on everything.
  const activeFilter: Filter = filter ?? ((counts.failed ?? 0) > 0 ? 'failed'
    : (counts.blocked ?? 0) > 0 ? 'blocked' : 'all')
  const shown = useMemo(() => (run?.cases || [])
    .filter((c) => activeFilter === 'all' || c.status === activeFilter), [run, activeFilter])

  // Keep a case picked: the focused one, else the first shown.
  useEffect(() => {
    if (!run) return
    if (picked && run.cases.some((c) => c.id === picked)) return
    setPicked(shown[0]?.id ?? null)
  }, [run, shown, picked])

  const groups = useMemo(() => {
    const m = new Map<string, RunCaseSummary[]>()
    for (const c of shown) {
      const k = c.object || (c.surface === 'site' ? 'Website' : 'Other')
      m.set(k, [...(m.get(k) || []), c])
    }
    return Array.from(m.entries())
  }, [shown])

  const live = !!run && isLive(run.status)
  const inBrowser = (run?.options?.driver ?? run?.driver) === 'chrome'
  const failedIds = (run?.cases || []).filter((c) => c.status === 'failed').map((c) => c.id)
  // Everything that did not pass — a case that couldn't run is re-tried too.
  const rerunIds = (run?.cases || []).filter((c) => c.status === 'failed' || c.status === 'blocked')
    .map((c) => c.id)

  const act = async (key: string, fn: () => Promise<void>) => {
    setBusy(key)
    try { await fn() } finally { setBusy(null) }
  }

  const onRegister = (caseIds?: string[]) => act(caseIds ? `bug-${caseIds[0]}` : 'bugs', async () => {
    try {
      const r = await syncDefects(project, run!.run_id, caseIds)
      const said = [r.created && `${r.created} new`, r.reattached && `${r.reattached} already known`]
        .filter(Boolean).join(', ')
      const rt = r.routing
      const auto = rt?.sent?.length ? ` ${rt.sent.length} sent to Support automatically.` : ''
      toast.success(`Bugs registered${said ? ` — ${said}` : ''}.${auto} Decide on the rest in Bugs.`)
      await loadOutcomes()
      await onChanged()
    } catch (err) {
      toast.error(apiError(err, 'Could not register the bugs'))
    }
  })

  const onRaiseTicket = (caseId: string) => act(`ticket-${caseId}`, async () => {
    try {
      const r = await raiseTicket(project, run!.run_id, caseId)
      toast.success(r.existing
        ? `${r.ref} is already Support issue #${r.issue}`
        : `Ticket raised — ${r.ref}${r.issue ? ` is Support issue #${r.issue}` : ''}`)
      await loadOutcomes()
      await onChanged()
    } catch (err) {
      toast.error(apiError(err, 'Could not raise the ticket'))
    }
  })

  const onReport = () => act('report', async () => {
    try {
      const docx = run!.report?.docx
      const path = docx || (await buildReport(project, run!.run_id)).docx.split('/').pop() || ''
      window.open(runFileUrl(project, run!.run_id, path), '_blank')
      if (!docx) setRun(await getRun(project, run!.run_id))
    } catch (err) {
      toast.error(apiError(err, 'Could not build the test report'))
    }
  })

  const onCancel = () => act('cancel', async () => {
    try {
      const ok = await cancelRun(project, run!.run_id)
      toast(ok ? 'Stopping — the case in progress finishes first' : 'That run is not going any more')
    } catch (err) {
      toast.error(apiError(err, 'Could not stop the run'))
    }
  })

  const onDelete = () => act('delete', async () => {
    if (!(await confirm({
      title: 'Delete run',
      message: 'Delete this run’s screenshots and results? Records it created stay listed under Test data and can still be cleaned.',
    }))) return
    try {
      await deleteRun(project, run!.run_id)
      toast.success('Run deleted')
      setSelected(null)
      setRun(null)
      await loadRuns()
      await onChanged()
    } catch (err) {
      toast.error(apiError(err, 'Could not delete the run'))
    }
  })

  if (runs && runs.length === 0) {
    return (
      <section style={{ ...panel, padding: 32, display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'flex-start' }}>
        <h2 style={{ margin: 0, fontSize: 21, fontWeight: 600 }}>No runs yet</h2>
        <p style={{ margin: 0, color: 'var(--color-text-secondary)', maxWidth: 620, lineHeight: 1.55 }}>
          A run opens the application in a real browser, follows every case step by step and keeps a
          screenshot of each step as evidence.
        </p>
        {canTest && (
          <Button icon={<Play size={15} />} loading={running} disabled={noCases} onClick={onRunAll}>Run all tests</Button>
        )}
      </section>
    )
  }

  const pill = (f: Filter, label: string, n: number, tone: Tone) => {
    const on = activeFilter === f
    return (
      <button key={f} type="button" aria-pressed={on} onClick={() => setFilter(f)} style={{
        display: 'inline-flex', alignItems: 'center', gap: 8, minHeight: 36, padding: '0 14px',
        borderRadius: 'var(--radius-full)', fontSize: 14, fontWeight: 600,
        border: `1px solid ${on ? TONE[tone].fg : 'var(--color-border)'}`,
        background: on ? TONE[tone].bg : 'transparent',
        color: on ? TONE[tone].fg : 'var(--color-text-secondary)',
      }}>{label} <strong>{n}</strong></button>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0, flex: '0 1 520px' }}>
          <label htmlFor="tw-run" style={labelCaps}>Run</label>
          <select id="tw-run" value={selected ?? ''} onChange={(e) => { setSelected(e.target.value); setFilter(null); setPicked(null) }}
                  style={{ ...inputStyle, fontSize: 16, fontWeight: 600, minHeight: 40, padding: '0 12px' }}>
            {(runs || []).map((r) => <option key={r.run_id} value={r.run_id}>{runLabel(r)}</option>)}
          </select>
        </div>
        {run && canTest && (
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {live ? (
              <Button variant="secondary" icon={<Square size={14} />} loading={busy === 'cancel'} onClick={onCancel}>
                Stop this run
              </Button>
            ) : (
              <>
                {rerunIds.length > 0 && (
                  <Button variant="secondary" icon={<RotateCcw size={15} />} loading={running}
                          onClick={() => void nav.runCases(rerunIds)}>
                    Run the {rerunIds.length} that didn’t pass again
                  </Button>
                )}
                {failedIds.length > 0 && (
                  <Button variant="secondary" loading={busy === 'bugs'} onClick={() => void onRegister()}>
                    Register the {failedIds.length} failure{failedIds.length === 1 ? '' : 's'} as bugs
                  </Button>
                )}
                <Button variant="secondary" icon={<Download size={15} />} loading={busy === 'report'}
                        onClick={() => void onReport()}>Download report</Button>
                <Button variant="ghost" icon={<Trash2 size={15} />} loading={busy === 'delete'}
                        onClick={() => void onDelete()} aria-label="Delete this run">Delete</Button>
              </>
            )}
          </div>
        )}
      </div>

      {run && live && (
        <div style={{ ...panel, padding: '14px 18px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          <span style={{ fontSize: 15 }}>
            {inBrowser && run.browser_session?.state === 'waiting'
              ? 'Waiting for the browser — open the start link Claude Code gave you in a Chrome tab.'
              : `Running${inBrowser ? ` in ${run.browser || 'your browser'}` : ''} — ${run.done ?? 0} of ${run.total ?? 0} cases done. Results appear as each case finishes.`}
          </span>
          {inBrowser && run.browser_session?.state === 'connected' && !run.needs_agent && (
            <span style={mutedText}>Leave that tab alone until the run finishes — the test is using it.</span>
          )}
          {run.needs_agent && (
            <span style={{ fontSize: 14, color: 'var(--color-warning)' }}>
              One action has to be done by hand in that tab ({run.needs_agent.reason || run.needs_agent.op}).
              Claude Code is asked to do it; the run carries on as soon as it is done.
            </span>
          )}
          <ShareBar label="Run progress" parts={[
            { value: counts.passed ?? 0, tone: 'ok' }, { value: counts.failed ?? 0, tone: 'err' },
            { value: counts.blocked ?? 0, tone: 'warn' },
            { value: Math.max(0, (run.total ?? 0) - (run.done ?? 0)), tone: 'neutral' },
          ]} />
        </div>
      )}
      {run && !live && inBrowser && (
        <div style={{ ...mutedText, fontSize: 14 }}>
          Ran in {run.browser || 'your own browser'}, driven from Claude Code. Its screenshots are drawn by
          the page itself, so a picture the page cannot reach may be missing from them.
        </div>
      )}
      {run?.error && (
        <div style={{ ...panel, padding: '14px 18px', color: 'var(--color-error)', fontSize: 15 }}>
          The run itself failed: {run.error}
        </div>
      )}
      {(run?.warnings || []).map((w, i) => (
        <div key={i} style={{ color: 'var(--color-warning)', fontSize: 14 }}>{w}</div>
      ))}

      {run && (
        <div role="group" aria-label="Show" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {pill('failed', 'Failed', counts.failed ?? 0, 'err')}
          {pill('blocked', 'Couldn’t run', counts.blocked ?? 0, 'warn')}
          {pill('passed', 'Passed', counts.passed ?? 0, 'ok')}
          {pill('all', 'All', run.cases.length, 'info')}
        </div>
      )}

      {!run ? <div style={mutedText}>Loading the run…</div> : (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20, alignItems: 'flex-start' }}>
          <section aria-label="Cases" style={{ ...panel, flex: '1 1 320px', minWidth: 0, overflow: 'hidden' }}>
            {groups.length === 0 && (
              <div style={{ ...mutedText, fontSize: 15, padding: 16 }}>
                {live ? 'Nothing here yet.' : 'No cases with this result.'}
              </div>
            )}
            {groups.map(([object, list]) => {
              const all = run.cases.filter((c) => (c.object || (c.surface === 'site' ? 'Website' : 'Other')) === object)
              const f = all.filter((c) => c.status === 'failed').length
              const p = all.filter((c) => c.status === 'passed').length
              return (
                <div key={object}>
                  <div style={{
                    padding: '12px 16px', display: 'flex', justifyContent: 'space-between', gap: 8,
                    borderTop: '1px solid var(--color-border)', marginTop: -1,
                  }}>
                    <strong style={{ fontSize: 15 }}>{nice(object, true)}</strong>
                    <span style={{ fontSize: 14, color: f ? 'var(--color-error)' : 'var(--color-text-secondary)' }}>
                      {f ? `${f} failed · ` : ''}{p} passed
                    </span>
                  </div>
                  {list.map((c) => {
                    const on = c.id === picked
                    const oc = outcomes[c.id]
                    const story = oc?.failed[0] ?? oc?.fixed_earlier[0]
                    return (
                      <button key={c.id} type="button" onClick={() => setPicked(c.id)} aria-current={on} style={{
                        width: '100%', textAlign: 'left', display: 'flex', gap: 12, alignItems: 'flex-start',
                        padding: '12px 16px', borderTop: '1px solid var(--color-border)',
                        background: on ? 'var(--color-accent-weak)' : 'transparent',
                        boxShadow: on ? 'inset 3px 0 0 var(--color-accent)' : 'none', color: 'var(--color-text)',
                      }}>
                        <StatusIcon status={c.status} />
                        <span style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
                          <span style={{ fontWeight: 600, fontSize: 15 }}>{c.title || c.id}</span>
                          <span style={{ ...mutedText, fontSize: 14 }}>{caseLine(c)}</span>
                          {story && (
                            <span style={{ fontSize: 13, fontWeight: 600, color: TONE[story.tone].fg }}>
                              {oc!.failed.length ? story.headline : `Failed earlier — ${story.headline}`}
                            </span>
                          )}
                        </span>
                      </button>
                    )
                  })}
                </div>
              )
            })}
          </section>

          <section aria-label="Case result" style={{ ...panel, flex: '999 1 600px', minWidth: 0, padding: 24 }}>
            {picked ? (
              <CaseDetail key={`${run.run_id}/${picked}`} project={project} run={run} caseId={picked}
                          canTest={canTest} canFix={canFix} busy={busy} outcome={outcomes[picked]}
                          onRegister={() => void onRegister([picked])}
                          onRaiseTicket={() => void onRaiseTicket(picked)}
                          onFix={(title, failure) => nav.fixCase(run.run_id, picked, title, failure,
                            run.cases.find((c) => c.id === picked)?.object || undefined)}
                          onRunAgain={() => void nav.runCases([picked])}
                          onEdit={() => setEditing(picked)} onBugs={() => nav.go('bugs')} />
            ) : <div style={mutedText}>Pick a case to see what happened.</div>}
          </section>
        </div>
      )}

      {editing && (
        <CaseEditor project={project} caseId={editing} canTest={canTest}
                    onClose={() => setEditing(null)} onChanged={onChanged} />
      )}
      {confirmDialog}
    </div>
  )
}

function caseLine(c: RunCaseSummary): string {
  if (c.status === 'failed' || c.status === 'blocked') {
    const msg = c.failure?.message || c.failure?.code || ''
    const at = c.failure?.step != null ? ` at step ${c.failure.step}` : ''
    const short = msg.length > 70 ? `${msg.slice(0, 70)}…` : msg
    return `${STATUS_WORD[c.status]}${at}${short ? ` · ${short}` : ''}`
  }
  return `${STATUS_WORD[c.status] ?? c.status}${c.duration_s != null ? ` · ${c.duration_s} s` : ''}`
}

function StatusIcon({ status }: { status: string }) {
  const tone = caseTone(status)
  const c = TONE[tone].fg
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true" style={{ flex: 'none', marginTop: 1 }}>
      <circle cx="9" cy="9" r="8" fill={c} />
      {status === 'passed' && <path d="M5.5 9.2l2.3 2.3 4.7-4.9" stroke="#fff" strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round" />}
      {status === 'failed' && <path d="M6 6l6 6M12 6l-6 6" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" />}
      {(status === 'blocked' || status === 'skipped') && <path d="M5.5 9h7" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" />}
    </svg>
  )
}

// ── one case of the run ─────────────────────────────────────────────────────

function CaseDetail({
  project, run, caseId, canTest, canFix, busy, outcome, onRegister, onRaiseTicket, onFix, onRunAgain,
  onEdit, onBugs,
}: {
  project: string; run: RunDetail; caseId: string; canTest: boolean; canFix: boolean; busy: string | null
  outcome?: CaseOutcome
  onRegister: () => void; onRaiseTicket: () => void; onFix: (title: string, failure: string) => void
  onRunAgain: () => void; onEdit: () => void; onBugs: () => void
}) {
  const summary = run.cases.find((c) => c.id === caseId)
  const [res, setRes] = useState<CaseResult | null>(null)
  const [consoleErrs, setConsoleErrs] = useState<unknown[] | null>(null)
  const [network, setNetwork] = useState<unknown[] | null>(null)
  const [light, setLight] = useState<{ src: string; caption?: string } | null>(null)
  const file = (name: string) => runFileUrl(project, run.run_id, `cases/${caseId}/${name}`)
  const done = summary && summary.status !== 'skipped' && !(isLive(run.status) && !summary.duration_s && !summary.failure)

  useEffect(() => {
    let alive = true
    ;(async () => {
      try {
        const r = await getCaseResult(project, run.run_id, caseId)
        if (alive) setRes(r)
      } catch (err) {
        // A case still queued in a live run has no result file yet.
        if (alive && !isLive(run.status)) toast.error(apiError(err, 'Could not load the case result'))
      }
      try {
        const [c, n] = await Promise.all([
          getRunJsonFile<unknown[]>(project, run.run_id, `cases/${caseId}/console.json`),
          getRunJsonFile<unknown[]>(project, run.run_id, `cases/${caseId}/network.json`),
        ])
        if (alive) { setConsoleErrs(c); setNetwork(n) }
      } catch {
        // Evidence files are optional; the result above still shows.
      }
    })()
    return () => { alive = false }
    // Re-read when the run moves on (a live run finishing this case).
  }, [project, run.run_id, run.done, run.status, caseId])

  if (!summary) return <div style={mutedText}>This case is not in the run.</div>
  const status = res?.status ?? summary.status
  const failed = status === 'failed' || status === 'blocked'
  const failStep = res?.steps.find((s) => s.n === (res.failure?.step ?? summary.failure?.step))
  const hasTrace = typeof res?.trace === 'boolean' ? res.trace
    : String(run.options?.trace ?? 'failed') === 'all' || (String(run.options?.trace ?? 'failed') === 'failed' && failed)
  const shots = (res?.steps || []).filter((s) => s.screenshot).slice(-5)
  const what = res?.failure?.message || summary.failure?.message || summary.failure?.code || ''

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <span style={{ color: 'var(--color-text-secondary)', fontFamily: 'var(--font-mono)', fontSize: 13 }}>{caseId}</span>
        <h2 style={{ margin: 0, fontSize: 23, fontWeight: 600, lineHeight: 1.3 }}>{summary.title || caseId}</h2>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', fontSize: 14, color: 'var(--color-text-secondary)' }}>
          <Badge tone={caseTone(status)}>{STATUS_WORD[status] ?? status}</Badge>
          <span>{summary.persona && summary.persona !== 'owner' ? `As ${nice(summary.persona)}` : 'As owner'}</span>
          {summary.duration_s != null && <span>· {summary.duration_s} s</span>}
          {(summary.requirement || []).length > 0 && <span>· proves {summary.requirement!.join(', ')}</span>}
        </div>
      </div>

      {!done && !res ? <div style={mutedText}>This case has not run yet.</div> : (
        <>
          {failed ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }}>
              <Outcome tone="ok" label="Should happen"
                       text={failStep ? shouldHappen(failStep.step) : 'Every step does what it should.'} />
              <Outcome tone={status === 'blocked' ? 'warn' : 'err'} label="What happened"
                       text={what || (status === 'blocked' ? 'The test could not get this far.' : 'The step did not do what it should.')} />
            </div>
          ) : (
            <Outcome tone="ok" label="Passed" text="Every step did what it should." />
          )}

          {outcome && outcome.failed.length > 0 && (
            <WhatHappened project={project} title="What happened next" stories={outcome.failed} />
          )}
          {outcome && outcome.fixed_earlier.length > 0 && (
            <WhatHappened project={project} title="This case failed earlier — and was fixed"
                          stories={outcome.fixed_earlier} />
          )}

          {res && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>What the test did</h3>
              <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column' }}>
                {res.steps.map((s) => {
                  const tone: Tone = s.status === 'passed' ? 'ok' : s.status === 'failed' ? 'err'
                    : s.status === 'blocked' ? 'warn' : 'neutral'
                  const notRun = s.status === 'skipped'
                  return (
                    <li key={`${s.kind}-${s.n}`} style={{
                      display: 'flex', gap: 12, alignItems: 'flex-start', padding: '8px 0',
                      borderTop: '1px solid var(--color-border)',
                    }}>
                      <NumberDot n={s.n} tone={tone} />
                      <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <span style={{
                          fontSize: 15, fontWeight: isCheck(s.step) ? 600 : 400,
                          color: notRun ? 'var(--color-text-faint)' : tone === 'err' ? 'var(--color-error)' : 'var(--color-text)',
                        }}>
                          {s.kind === 'precondition' ? 'Before the test: ' : ''}{stepText(s.step)}
                          {notRun ? ' (not run after the failure)' : ''}
                        </span>
                        {s.by_hand && (
                          <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
                            Done by hand in the browser — the page could not do it by script.
                          </span>
                        )}
                        {s.status !== 'passed' && !notRun && (s.error || s.result) && (
                          <span style={{ fontSize: 14, color: 'var(--color-text-secondary)', whiteSpace: 'pre-wrap' }}>
                            {s.error || s.result}
                          </span>
                        )}
                      </span>
                      <span style={{ ...mutedText, whiteSpace: 'nowrap' }}>
                        {s.duration_s != null ? `${s.duration_s} s` : '—'}
                      </span>
                    </li>
                  )
                })}
              </ol>
            </div>
          )}

          {res && (failed || shots.length > 0) && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Screenshots</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: 12 }}>
                {failed && (
                  <Shot src={file('failure.png')} caption="The page when it failed" fail
                        onOpen={(src, caption) => setLight({ src, caption })} />
                )}
                {shots.map((s) => (
                  <Shot key={s.n} src={file(s.screenshot!)} caption={`Step ${s.n} · ${stepText(s.step)}`}
                        fail={s.status === 'failed'} onOpen={(src, caption) => setLight({ src, caption })} />
                ))}
              </div>
            </div>
          )}

          {res && (
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 14, alignItems: 'center' }}>
              {hasTrace && (
                <a href={file('trace.zip')} download title="Open it at trace.playwright.dev to replay every action">
                  <Download size={13} /> Replay every step (trace)
                </a>
              )}
              <span style={{ color: consoleErrs?.length ? 'var(--color-warning)' : 'var(--color-text-secondary)' }}>
                Browser console: {consoleErrs?.length ? `${consoleErrs.length} error(s)` : 'clean'}
              </span>
              {network && network.length > 0 && (
                <span style={{ color: 'var(--color-warning)' }}>{network.length} failed server call(s)</span>
              )}
              {res.server_log && (
                <a href={file(res.server_log)} target="_blank" rel="noreferrer">
                  <ExternalLink size={13} /> Server log for this case
                </a>
              )}
            </div>
          )}
          {res && ((consoleErrs?.length ?? 0) > 0 || (network?.length ?? 0) > 0) && (
            <details>
              <summary style={{ cursor: 'pointer', ...mutedText, fontSize: 14 }}>For specialists: console and network details</summary>
              {consoleErrs && consoleErrs.length > 0 && (
                <pre style={{ fontSize: 13, maxHeight: 200, overflow: 'auto', margin: '8px 0 0' }}>{JSON.stringify(consoleErrs, null, 2)}</pre>
              )}
              {network && network.length > 0 && (
                <pre style={{ fontSize: 13, maxHeight: 200, overflow: 'auto', margin: '8px 0 0' }}>{JSON.stringify(network, null, 2)}</pre>
              )}
            </details>
          )}
          {res && (
            <div style={{ ...mutedText, display: 'flex', flexDirection: 'column', gap: 4 }}>
              {(res.created_records || []).length > 0 && (
                <span>Records it created: {res.created_records!.map((r) => `${nice(r.obj, true)} ${r.txn_id}`).join(', ')}</span>
              )}
              {(res.native_dialogs || []).length > 0 && (
                <span>Browser dialogs it accepted: {res.native_dialogs!.map((d) => `“${d.text}”`).join(', ')}</span>
              )}
              {(res.blocked_navigations || []).length > 0 && (
                <span>Links off the application it was kept from following: {res.blocked_navigations!.join(', ')}</span>
              )}
              {res.started_at && <span>Ran {ago(res.started_at)} ({fmtTime(res.started_at)}){took(res.started_at, res.finished_at) ? ` · ${took(res.started_at, res.finished_at)}` : ''}</span>}
            </div>
          )}

          {canTest && !isLive(run.status) && (
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', paddingTop: 16, borderTop: '1px solid var(--color-border)' }}>
              {/* A developer / project manager fixes it right here (no Support
                  ticket); quality control raises a ticket. Both can re-test. */}
              {failed && canFix && (
                <Button icon={<Sparkles size={15} />}
                        onClick={() => onFix(summary.title || caseId, what)}>Fix with AI and re-test</Button>
              )}
              {status === 'failed' && !canFix && (
                <Button icon={<Ticket size={15} />} loading={busy === `ticket-${caseId}`}
                        onClick={onRaiseTicket}>Raise a ticket</Button>
              )}
              {status === 'failed' && canFix && !outcome?.failed.length && (
                <Button variant="secondary" loading={busy === `bug-${caseId}`} onClick={onRegister}>Register as bug</Button>
              )}
              <Button variant="secondary" icon={<RotateCcw size={15} />} onClick={onRunAgain}>Run this case again</Button>
              {failed && <Button variant="ghost" onClick={onEdit}>The test is wrong — edit case</Button>}
              {!failed && <Button variant="ghost" onClick={onEdit}>Edit case</Button>}
              {status === 'failed' && (
                <button type="button" onClick={onBugs} style={{ fontSize: 14, color: 'var(--color-accent)', padding: '0 4px' }}>
                  Go to bugs →
                </button>
              )}
            </div>
          )}
        </>
      )}
      <Lightbox src={light?.src ?? null} caption={light?.caption} onClose={() => setLight(null)} />
    </div>
  )
}

function WhatHappened({ project, title, stories }: { project: string; title: string; stories: DefectStory[] }) {
  const issueUrl = (n: number) => `/workbench/${encodeURIComponent(project)}/advanced/support?issue=${n}`
  return (
    <section aria-label={title} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>{title}</h3>
      {stories.map((st) => (
        <div key={st.id} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <Badge tone={st.tone}>{st.headline}</Badge>
            <span style={{ ...mutedText, fontFamily: 'var(--font-mono)', fontSize: 13 }}>{st.ref}</span>
            {st.support && (
              <Link to={issueUrl(st.support.number)} style={{ fontSize: 14 }}>
                <ExternalLink size={13} /> Open Support issue #{st.support.number}
              </Link>
            )}
          </div>
          <ol style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {st.steps.map((s, i) => (
              <li key={`${s.kind}-${i}`} style={{
                display: 'flex', gap: 12, alignItems: 'flex-start', padding: '8px 0',
                borderTop: '1px solid var(--color-border)',
              }}>
                <span aria-hidden="true" style={{
                  width: 10, height: 10, borderRadius: '50%', flex: 'none', marginTop: 6, background: TONE[s.tone].fg,
                }} />
                <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <span style={{ fontSize: 15 }}>
                    {s.title}
                    {s.issue != null && s.kind !== 'needed' && (
                      <> · <Link to={issueUrl(s.issue)} style={{ fontSize: 14 }}>#{s.issue}</Link></>
                    )}
                  </span>
                  {s.detail && (
                    <span style={{ fontSize: 14, color: 'var(--color-text-secondary)', whiteSpace: 'pre-wrap' }}>{s.detail}</span>
                  )}
                </span>
                {s.at && <span style={{ ...mutedText, whiteSpace: 'nowrap' }}>{fmtTime(s.at)}</span>}
              </li>
            ))}
          </ol>
        </div>
      ))}
    </section>
  )
}

function Outcome({ tone, label, text }: { tone: Tone; label: string; text: string }) {
  return (
    <div style={{
      border: `1px solid ${TONE[tone].fg}`, background: TONE[tone].bg, borderRadius: 10, padding: '14px 16px',
      display: 'flex', flexDirection: 'column', gap: 6,
    }}>
      <span style={{ color: TONE[tone].fg, fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
        {label}
      </span>
      <span style={{ lineHeight: 1.5, fontSize: 15 }}>{text}</span>
    </div>
  )
}

function Shot({ src, caption, fail, onOpen }: {
  src: string; caption: string; fail?: boolean; onOpen: (src: string, caption?: string) => void
}) {
  const [missing, setMissing] = useState(false)
  if (missing) return null
  return (
    <figure style={{ margin: 0, display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
      <div style={{
        borderRadius: 8, overflow: 'hidden', lineHeight: 0,
        border: `2px solid ${fail ? 'var(--color-error)' : 'var(--color-border)'}`,
      }}>
        <Thumb src={src} caption={caption} onOpen={onOpen} fluid onMissing={() => setMissing(true)} />
      </div>
      <figcaption style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>{caption}</figcaption>
    </figure>
  )
}
